/**
 * Company API Contract
 *
 * Mirrors: backend/src/routes/company.routes.ts
 * 1:1 parity with existing Express routes
 *
 * Note: Location endpoints (/companies/:companyId/locations) are in location.ts
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { UpdateOrganizationProfileInputSchema, OrganizationProfileResponseSchema, } from "@hourly/api/organizationProfile";
import { uuidSchema } from "@hourly/api/validation-common";
import { CompanyResponseSchema, CompanyWithOwnerResponseSchema, CorporateStructureResponseSchema, CreateCompanyInputSchema, UpdateCompanyInputSchema, OnboardCompanyInputSchema, } from "@hourly/api/company";
// ============================================
// Company CRUD Contracts
// ============================================
/**
 * POST /api/companies
 * Create a new company
 */
export const createCompany = oc
    .route({
    method: "POST",
    path: "/companies",
    summary: "Create a new company",
    tags: ["Companies"],
    successStatus: 201,
})
    .input(CreateCompanyInputSchema)
    .output(CompanyResponseSchema);
/**
 * GET /api/companies
 * Get all companies
 */
export const getAllCompanies = oc
    .route({
    method: "GET",
    path: "/companies",
    summary: "List all companies",
    tags: ["Companies"],
})
    .input(z.object({}))
    .output(z.array(CompanyResponseSchema));
/**
 * GET /api/companies/:id
 * Get a company by ID
 */
export const getCompany = oc
    .route({
    method: "GET",
    path: "/companies/{id}",
    summary: "Get company by ID",
    tags: ["Companies"],
})
    .input(z.object({ id: uuidSchema }))
    .output(CompanyResponseSchema);
/**
 * PUT /api/companies/:id
 * Update a company
 */
export const updateCompany = oc
    .route({
    method: "PUT",
    path: "/companies/{id}",
    summary: "Update a company",
    tags: ["Companies"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateCompanyInputSchema))
    .output(CompanyResponseSchema);
/**
 * DELETE /api/companies/:id
 * Delete a company
 */
export const deleteCompany = oc
    .route({
    method: "DELETE",
    path: "/companies/{id}",
    summary: "Delete a company",
    tags: ["Companies"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// ============================================
// Onboarding Contract
// ============================================
/**
 * POST /api/companies/onboard
 * Self-service company onboarding - creates company and assigns user as owner
 */
export const onboardCompany = oc
    .route({
    method: "POST",
    path: "/companies/onboard",
    summary: "Self-service company onboarding",
    tags: ["Companies"],
    successStatus: 201,
})
    .input(OnboardCompanyInputSchema)
    .output(CompanyWithOwnerResponseSchema);
// ============================================
// Corporate Structure Contract
// ============================================
/**
 * GET /corporate-structure
 * Get corporate structure (shimmed for frontend compatibility)
 * Uses companyId from JWT context - NO companyId in path or input
 * Note: Moved out of /companies namespace to avoid routing conflict
 */
export const getCorporateStructure = oc
    .route({
    method: "GET",
    path: "/corporate-structure",
    summary: "Get corporate structure",
    tags: ["Companies"],
})
    .input(z.object({}))
    .output(CorporateStructureResponseSchema);
;
// ============================================
// Organization Profile Contracts
// ============================================
/**
 * GET /organization-profile
 * Get organization profile for a company
 * Uses companyId from JWT context - NO companyId in path or input
 * Note: Moved out of /companies namespace to avoid routing conflict
 */
export const getOrganizationProfile = oc
    .route({
    method: "GET",
    path: "/organization-profile",
    summary: "Get organization profile",
    tags: ["Companies"],
})
    .input(z.object({}))
    .output(OrganizationProfileResponseSchema);
;
/**
 * PUT /organization-profile
 * Update organization profile for a company
 * Uses companyId from JWT context - NO companyId in path or input
 * Note: Moved out of /companies namespace to avoid routing conflict
 */
export const updateOrganizationProfile = oc
    .route({
    method: "PUT",
    path: "/organization-profile",
    summary: "Update organization profile",
    tags: ["Companies"],
})
    .input(UpdateOrganizationProfileInputSchema)
    .output(OrganizationProfileResponseSchema);
// ============================================
// Contract Router
// ============================================
export const companyContract = {
    // CRUD operations
    create: createCompany,
    getAll: getAllCompanies,
    get: getCompany,
    update: updateCompany,
    delete: deleteCompany,
    // Onboarding
    onboard: onboardCompany,
    // Corporate Structure
    getCorporateStructure,
    // Organization Profile
    getOrganizationProfile,
    updateOrganizationProfile,
};
//# sourceMappingURL=company.js.map